import { userScopedKey } from "./later";

/**
 * People you've opened a DM with but haven't messaged yet.
 *
 * CometChat's ConversationsRequest only returns conversations that
 * actually contain a message, so picking someone out of search used to
 * open their chat and then vanish from the sidebar the moment you looked
 * away — nothing to click back to unless you'd already typed something.
 * That's the whole route an external user has to reach anyone (they get
 * no directory list), so it can't rely on having sent a message first.
 *
 * Kept per browser, per signed-in uid, and only ever a fallback: once a
 * real conversation exists for that uid the sidebar prefers it, and this
 * entry is skipped as a duplicate.
 */

const KEY = "dbm-chat-opened-dms";
/** Enough to cover "people I'm mid-conversation with", not a second
 * inbox — the real conversation list takes over as soon as anyone speaks. */
const MAX = 30;

export const OPENED_DMS_EVENT = "dbm-opened-dms-changed";

export type OpenedDm = { uid: string; name: string };

export function loadOpenedDms(): OpenedDm[] {
  try {
    const raw = localStorage.getItem(userScopedKey(KEY));
    const list = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    return list.filter(
      (x): x is OpenedDm => !!x && typeof x.uid === "string" && typeof x.name === "string"
    );
  } catch {
    return [];
  }
}

function save(list: OpenedDm[]): void {
  try {
    localStorage.setItem(userScopedKey(KEY), JSON.stringify(list));
  } catch {
    // A full/blocked localStorage shouldn't break opening a chat.
  }
  window.dispatchEvent(new CustomEvent(OPENED_DMS_EVENT));
}

export function rememberOpenedDm(uid: string, name: string): void {
  if (!uid) return;
  const list = loadOpenedDms();
  const existing = list.find((o) => o.uid === uid);
  // Re-opening someone already at the top shouldn't churn storage or fire
  // an event that re-renders the sidebar for no visible change.
  if (existing && list[0]?.uid === uid && existing.name === name) return;
  save([{ uid, name }, ...list.filter((o) => o.uid !== uid)].slice(0, MAX));
}

export function forgetOpenedDm(uid: string): void {
  const list = loadOpenedDms();
  if (!list.some((o) => o.uid === uid)) return;
  save(list.filter((o) => o.uid !== uid));
}
